import { reactive, computed, watch } from 'vue'

type AccessibilityState = {
  highContrast: boolean
  largeText: boolean
  reducedMotion: boolean
  focusIndicators: boolean
  keyboardNavigation: boolean
  screenReaderOptimized: boolean
  colorBlindSupport: boolean
  dyslexiaFriendly: boolean
  darkMode: boolean
  fontSize: 'small' | 'medium' | 'large' | 'extra-large'
  lineHeight: 'tight' | 'normal' | 'relaxed'
  letterSpacing: 'tight' | 'normal' | 'wide'
}

const STORAGE_KEY = 'a11ySettings'

const state = reactive<AccessibilityState>({
  highContrast: false,
  largeText: false,
  reducedMotion: false,
  focusIndicators: false,
  keyboardNavigation: true,
  screenReaderOptimized: false,
  colorBlindSupport: false,
  dyslexiaFriendly: false,
  darkMode: false,
  fontSize: 'medium',
  lineHeight: 'normal',
  letterSpacing: 'normal',
})

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) Object.assign(state, JSON.parse(raw))
  } catch {}
  apply()
}

function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

function apply() {
  const root = document.documentElement

  const toggles = [
    'highContrast',
    'largeText',
    'reducedMotion',
    'focusIndicators',
    'keyboardNavigation',
    'screenReaderOptimized',
    'colorBlindSupport',
    'dyslexiaFriendly',
    'darkMode',
  ] as const

  toggles.forEach((toggle) => {
    if (state[toggle]) {
      root.classList.add(`a11y-${toggle.replace(/([A-Z])/g, '-$1').toLowerCase()}`)
    } else {
      root.classList.remove(`a11y-${toggle.replace(/([A-Z])/g, '-$1').toLowerCase()}`)
    }
  })

  root.classList.remove(
    'a11y-font-small',
    'a11y-font-medium',
    'a11y-font-large',
    'a11y-font-extra-large',
  )
  root.classList.add(`a11y-font-${state.fontSize}`)

  root.classList.remove('a11y-line-tight', 'a11y-line-normal', 'a11y-line-relaxed')
  root.classList.add(`a11y-line-${state.lineHeight}`)

  root.classList.remove('a11y-spacing-tight', 'a11y-spacing-normal', 'a11y-spacing-wide')
  root.classList.add(`a11y-spacing-${state.letterSpacing}`)
}

export default function useAccessibility() {
  if (typeof window !== 'undefined') load()

  watch(
    state,
    () => {
      apply()
      save()
    },
    { deep: true },
  )

  const highContrast = computed(() => state.highContrast)
  const largeText = computed(() => state.largeText)
  const reducedMotion = computed(() => state.reducedMotion)
  const focusIndicators = computed(() => state.focusIndicators)
  const keyboardNavigation = computed(() => state.keyboardNavigation)
  const screenReaderOptimized = computed(() => state.screenReaderOptimized)
  const colorBlindSupport = computed(() => state.colorBlindSupport)
  const dyslexiaFriendly = computed(() => state.dyslexiaFriendly)
  const darkMode = computed(() => state.darkMode)
  const fontSize = computed(() => state.fontSize)
  const lineHeight = computed(() => state.lineHeight)
  const letterSpacing = computed(() => state.letterSpacing)

  const toggleHighContrast = () => {
    state.highContrast = !state.highContrast
    apply()
    save()
  }
  const toggleLargeText = () => (state.largeText = !state.largeText)
  const toggleReducedMotion = () => (state.reducedMotion = !state.reducedMotion)
  const toggleFocusIndicators = () => (state.focusIndicators = !state.focusIndicators)
  const toggleKeyboardNavigation = () => (state.keyboardNavigation = !state.keyboardNavigation)
  const toggleScreenReaderOptimized = () =>
    (state.screenReaderOptimized = !state.screenReaderOptimized)
  const toggleColorBlindSupport = () => (state.colorBlindSupport = !state.colorBlindSupport)
  const toggleDyslexiaFriendly = () => (state.dyslexiaFriendly = !state.dyslexiaFriendly)
  const toggleDarkMode = () => (state.darkMode = !state.darkMode)

  const setFontSize = (size: 'small' | 'medium' | 'large' | 'extra-large') => {
    state.fontSize = size
  }
  const setLineHeight = (height: 'tight' | 'normal' | 'relaxed') => {
    state.lineHeight = height
  }
  const setLetterSpacing = (spacing: 'tight' | 'normal' | 'wide') => {
    state.letterSpacing = spacing
  }

  return {
    highContrast,
    largeText,
    reducedMotion,
    focusIndicators,
    keyboardNavigation,
    screenReaderOptimized,
    colorBlindSupport,
    dyslexiaFriendly,
    darkMode,
    fontSize,
    lineHeight,
    letterSpacing,

    toggleHighContrast,
    toggleLargeText,
    toggleReducedMotion,
    toggleFocusIndicators,
    toggleKeyboardNavigation,
    toggleScreenReaderOptimized,
    toggleColorBlindSupport,
    toggleDyslexiaFriendly,
    toggleDarkMode,

    setFontSize,
    setLineHeight,
    setLetterSpacing,
  }
}
